import React, { Fragment, useState, useEffect } from 'react'
import propTypes from 'prop-types'
import { useForm } from 'react-hook-form'

/** Compont and material ui */
import { ButtonGroup, Button, Tooltip, TextField, Typography, IconButton, Chip } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import Add from '@material-ui/icons/Add'
import Delete from '@material-ui/icons/Delete'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import StorageIcon from '@material-ui/icons/Storage'

/**Common and componets import */
import DatagridCustom from './DataGridPartialities'
import Select from '../../common/ReactSelectAvatar'
import DateSelector from '../../common/DateSelector'
import ExistencesTable from '../../spareParts/components/manual/ExistencesTable'
import { DialogView } from '../../common/Dialog'
import AlertComponent from '../../common/AlertComponent'

/**Resources */
import { hasAuthorization } from '../../../store/session/sessionActions'
import { GetWarehouse, GetCarriers, GetSparePartSaleStatus } from '../../../store/helpers/SelectOptions'
import { SPARE_PARTS_IMBERA_USA, SPARE_PARTS_IMBERA_MX, PORTAL_ADMINISTRATOR, IMBERA_TECHNICIAN, PAYMENT_MANAGER } from '../../../resources/RolesEnum'
import { SpareParRequestStatus } from '../../../store/helpers/AppConstants'
import { getStatusColor } from '../../../store/helpers/sparePartsStatusColors'
/**
 * Derives the parent's requested/remaining quantities from its active (non-cancelled) partiality children.
 * `PartialQuantityTotal`, when the backend provides it, is the ALLOCATED amount (sum of active partial
 * quantities so far) — not the remaining balance — so it must be subtracted from Quantity, same as the
 * client-derived `allocated` fallback below.
 */
export const getPartialTotals = (item, children) => {
	const activeChildren = (children || []).filter((child) => child.SparePartSaleStatus !== 2)
	const derivedAllocated = activeChildren.reduce((sum, child) => sum + (child.PartialQuantity || 0), 0)
	const allocated = item.PartialQuantityTotal != null ? item.PartialQuantityTotal : derivedAllocated
	const totalParcialidad = Math.max(0, item.Quantity - allocated)
	return {
		totalSolicitada: item.Quantity,
		totalParcialidad
	}
}

/**
 * Component containing the list of spare parts requested for a equipment
 */
const SparePartList = (props) => {
	const {
		isAuthorizadoForEditedData,
		onUpdateSpartPart,
		userProfile,
		status,
		onEditLateralPanelConfig,
		//onSendEmailToTechnical,
		//IsPayment,
		identifierExpanded,
		setIdentifierExpanded,
		isCustomerUser,
		isGetStock,
		onAddPartiality,
		sparePartSalesToken,
		isSaving,
		ShowAddPartiality
	} = props
	const { errors, control } = useForm()
	const [positionErrors, setPositionErrors] = useState({})
	const [partialityDialog, setPartialityDialog] = useState({ open: false, item: null })
	const [partialityQuantity, setPartialityQuantity] = useState(1)
	const warehouseOptions = GetWarehouse()
	const carriersOptions = GetCarriers()
	const sparePartSaleStatusOptions = GetSparePartSaleStatus()
	let isAuthorizerCanEditRoleImberaMx =
		hasAuthorization(userProfile, [SPARE_PARTS_IMBERA_MX]) &&
		(status === SpareParRequestStatus.SEND_TO_MX || status === SpareParRequestStatus.DRAFT)
	let isAuthorizerCanEditRoleImberaUsa =
		hasAuthorization(userProfile, [SPARE_PARTS_IMBERA_USA]) &&
		(status === SpareParRequestStatus.IN_PROCESS || status === SpareParRequestStatus.SEND_TO_MX || status === SpareParRequestStatus.DRAFT)

	let isAdmin = hasAuthorization(userProfile, [PORTAL_ADMINISTRATOR])
	let canEditPosition = hasAuthorization(userProfile, [PORTAL_ADMINISTRATOR, SPARE_PARTS_IMBERA_USA, SPARE_PARTS_IMBERA_MX])

	const updateSpartPart = (item, name, value, propertyToUpdate = '') => {
		if (onUpdateSpartPart) {
			const updatedItem = { ...item, [name]: value }
			onUpdateSpartPart(updatedItem, propertyToUpdate)
		}
	}

	let isAuthorizerCanBeGetExistencesFromSap = hasAuthorization(userProfile, [
		PORTAL_ADMINISTRATOR,
		IMBERA_TECHNICIAN,
		SPARE_PARTS_IMBERA_MX,
		PAYMENT_MANAGER
	])

	// Parent expand/collapse state for master-detail
	const [expandedParents, setExpandedParents] = useState(new Set())

	// Auto-expand parents that have children
	useEffect(() => {
		if (props.spareParts && props.spareParts.length > 0) {
			const childParentIds = new Set(props.spareParts.filter((sp) => sp.ParentId !== null).map((sp) => sp.ParentId))
			if (childParentIds.size > 0) {
				setExpandedParents((prev) => {
					const next = new Set(prev)
					childParentIds.forEach((id) => next.add(id))
					return next
				})
			}
		}
	}, [props.spareParts])

	const toggleParentExpanded = (parentId) => {
		setExpandedParents((prev) => {
			const next = new Set(prev)
			if (next.has(parentId)) {
				next.delete(parentId)
			} else {
				next.add(parentId)
			}
			return next
		})
	}

	const expandAllParents = () => {
		if (!props.spareParts) return
		const allParentIds = props.spareParts.filter((sp) => sp.ParentId === null).map((sp) => sp.Id)
		setExpandedParents(new Set(allParentIds))
	}

	const collapseAllParents = () => {
		setExpandedParents(new Set())
	}

	const openPartialityDialog = (item) => {
		const children = props.spareParts.filter((sp) => sp.ParentId === item.Id)
		const { totalParcialidad } = getPartialTotals(item, children)
		// The parent must always keep at least 1 unit for itself, so the max a new partiality can take is remaining - 1
		const maxPartialityQuantity = totalParcialidad - 1
		setPartialityQuantity(maxPartialityQuantity > 0 ? maxPartialityQuantity : 1)
		setPartialityDialog({ open: true, item })
	}

	const closePartialityDialog = () => {
		setPartialityDialog({ open: false, item: null })
	}

	const handleAddPartialities = async () => {
		const { item } = partialityDialog
		closePartialityDialog()
		await onAddPartiality(sparePartSalesToken, item.Code, partialityQuantity)
	}

	/**
	 * Get the configuration table
	 */
	const getParentColumnsInternal = () => {
		let config = parentColumnsInternal.map((col) => ({ ...col }))
		// Remove Position column if status is DRAFT
		if (status === SpareParRequestStatus.DRAFT) {
			config = config.filter((col) => col.dataSource !== 'Position')
		}

		let descriptionProperty = config.find((x) => x.dataSource === 'Description')
		descriptionProperty.onRenderProperty = (item) => {
			return (
				<div style={{ width: '150px' }}>
					<Typography variant='caption'>
						{`${item.Code} | ${item.Description}`} | {<strong style={{ color: 'red' }}>Quantity {item.Quantity}</strong>}
					</Typography>
				</div>
			)
		}

		let parentSparePartSaleStatusProperty = config.find((x) => x.dataSource === 'SparePartSaleStatus')
		parentSparePartSaleStatusProperty.onRenderProperty = (item) => {
			let canBeEditStatus =
				isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<div style={{ width: '115px' }}>
					<Select
						key={'ParentSparePartSaleStatus'}
						fullWidth
						defaultValue={item.SparePartSaleStatus}
						options={sparePartSaleStatusOptions}
						name='SparePartSaleStatus'
						isDisabled={!canBeEditStatus}
						value={item.SparePartSaleStatus ? sparePartSaleStatusOptions.find((x) => x.value === item.SparePartSaleStatus) : null}
						onChange={(name, option) => updateSpartPart(item, 'SparePartSaleStatus', option.value)}
						margin='dense'
						register={null}
						errors={errors}
						control={control}
					/>
				</div>
			)
		}

		let totalParcialidadProperty = config.find((x) => x.dataSource === 'TotalParcialidad')
		totalParcialidadProperty.onRenderProperty = (item) => {
			const children = props.spareParts.filter((sp) => sp.ParentId === item.Id)
			const { totalParcialidad } = getPartialTotals(item, children)
			return totalParcialidad
		}

		let positionProperty = config.find((x) => x.dataSource === 'Position')
		if (positionProperty) {
			positionProperty.onRenderProperty = (item) => {
				return (
					<TextField
						style={{ width: '60px' }}
						key={`position_${item.Token}`}
						fullWidth
						disabled={!canEditPosition}
						defaultValue={item.Position}
						error={!!positionErrors[item.Token]}
						helperText={positionErrors[item.Token] || ''}
						onBlur={(event) => {
							const newValue = event.target.value
							const isDuplicate = props.spareParts
								.filter((sp) => sp.ParentId === null)
								.some((sp) => sp.Token !== item.Token && String(sp.Position) === String(newValue))
							if (isDuplicate) {
								setPositionErrors((prev) => ({ ...prev, [item.Token]: `Position ${newValue} already exists` }))
								return
							}
							setPositionErrors((prev) => ({ ...prev, [item.Token]: null }))
							updateSpartPart(item, 'Position', newValue, 'POSITION')
						}}
						margin='dense'
						variant='outlined'
						inputProps={{
							style: { fontSize: 12 }
						}}
					/>
				)
			}
		}

		let parentWarehouseProperty = config.find((x) => x.dataSource === 'Warehouse')
		parentWarehouseProperty.onRenderProperty = (item) => {
			return (
				<div style={{ width: '90px' }}>
					<Select
						key={'ParentWarehouse'}
						fullWidth
						defaultValue={item.Warehouse}
						options={warehouseOptions}
						name='Warehouse'
						isDisabled={!(isAdmin || isAuthorizerCanEditRoleImberaUsa)}
						value={item.Warehouse ? warehouseOptions.find((x) => x.value === item.Warehouse) : null}
						onChange={(name, option) => updateSpartPart(item, 'Warehouse', option.value, 'WAREHOUSE')}
						margin='dense'
						register={null}
						errors={errors}
						control={control}
					/>
				</div>
			)
		}

		const getCanShipAsParent = (item) => {
			const children = props.spareParts.filter((sp) => sp.ParentId === item.Id)
			const { totalParcialidad } = getPartialTotals(item, children)
			return totalParcialidad > 0
		}

		let parentNumberTrackingProperty = config.find((x) => x.dataSource === 'NumberTracking')
		parentNumberTrackingProperty.onRenderProperty = (item) => {
			if (!getCanShipAsParent(item)) return null
			let editCanbe = isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<TextField
					style={{ width: '160px' }}
					key={'parentNumberTraking'}
					fullWidth
					disabled={!editCanbe}
					defaultValue={item.NumberTracking}
					onBlur={(event) => updateSpartPart(item, 'NumberTracking', event.target.value)}
					margin='dense'
					variant='outlined'
					inputProps={{
						style: { fontSize: 12 }
					}}
				/>
			)
		}

		let parentCarrierProperty = config.find((x) => x.dataSource === 'Carrier')
		parentCarrierProperty.onRenderProperty = (item) => {
			if (!getCanShipAsParent(item)) return null
			let editCanbe = isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<div style={{ width: '120px' }}>
					<Select
						key={'ParentCarrier'}
						fullWidth
						defaultValue={item.Carrier}
						options={carriersOptions}
						name='Carrier'
						isDisabled={!editCanbe}
						value={item.Carrier ? carriersOptions.find((x) => x.value === item.Carrier || x.oldvalue === item.Carrier) : null}
						onChange={(name, option) => updateSpartPart(item, 'Carrier', option.value)}
						margin='dense'
						register={null}
						errors={errors}
						control={control}
					/>
					{item.IsOtherCarrier && (
						<TextField
							key={'parentOtherCarrier'}
							fullWidth
							disabled={!editCanbe}
							defaultValue={item.OtherCarrier}
							onBlur={(event) => updateSpartPart(item, 'OtherCarrier', event.target.value)}
							margin='dense'
							variant='outlined'
							inputProps={{
								style: { fontSize: 12 }
							}}
						/>
					)}
				</div>
			)
		}

		let parentShipDateProperty = config.find((x) => x.dataSource === 'ShipDate')
		parentShipDateProperty.onRenderProperty = (item) => {
			if (!getCanShipAsParent(item)) return null
			let editCanbe = isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<div style={{ width: '100px' }}>
					<DateSelector
						showIncon={true}
						dataCy='parentDate'
						name='shipDate'
						value={item.ShipDate}
						disabled={!editCanbe}
						onChange={(event) => updateSpartPart(item, 'ShipDate', event.target.value)}
					/>
				</div>
			)
		}

		let parentStatusProperty = config.find((x) => x.dataSource === 'Status')
		parentStatusProperty.onRenderProperty = (item) => {
			if (!getCanShipAsParent(item)) return null
			const chipColor = getStatusColor(item.Status)
			return (
				<Chip
					label={chipColor === 'lightgray' ? 'Not found' : item?.Status}
					style={{
						backgroundColor: chipColor,
						color: 'white'
					}}
				/>
			)
		}

		let PoProperty = config.find((x) => x.dataSource === 'PoNumber')
		PoProperty.onRenderProperty = (item) => {
			let editCanbe = isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<TextField
					style={{ width: '100px' }}
					key={'Po'}
					fullWidth
					disabled={!editCanbe}
					defaultValue={item.PoNumber}
					onBlur={(event) => updateSpartPart(item, 'PoNumber', event.target.value, 'PoNumber')}
					margin='dense'
					variant='outlined'
					inputProps={{
						style: { fontSize: 12 }
					}}
				/>
			)
		}

		let referenceSAPProperty = config.find((x) => x.dataSource === 'ReferenceSAP')
		referenceSAPProperty.onRenderProperty = (item) => {
			let editCanbe =
				isAdmin ||
				(((isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)) &&
					status !== SpareParRequestStatus.DRAFT)
			return (
				<TextField
					style={{ width: '100px' }}
					key={'referenceSAP'}
					fullWidth
					disabled={!editCanbe}
					defaultValue={item.ReferenceSAP}
					onBlur={(event) => updateSpartPart(item, 'ReferenceSAP', event.target.value, 'ReferenceSAP')}
					margin='dense'
					variant='outlined'
					inputProps={{
						style: { fontSize: 12 }
					}}
				/>
			)
		}

		let unitPriceProperty = config.find((x) => x.dataSource === 'UnitPrice')
		unitPriceProperty.onRenderProperty = (item) => {
			return item.UnitPrice
		}

		let unitTotalProperty = config.find((x) => x.dataSource === 'Total')
		unitTotalProperty.onRenderProperty = (item) => {
			return item.Total
		}

		let lastUpdateProperty = config.find((x) => x.dataSource === 'LastUpdateUser')
		lastUpdateProperty.onRenderProperty = (item) => {
			return (
				<div style={{ width: '120px' }}>
					<Typography variant='caption'>{`${item.LastUpdateUserName} ${item.LastUpdateDate}`}</Typography>
				</div>
			)
		}

		// Options column
		if (isAuthorizadoForEditedData && status === SpareParRequestStatus.DRAFT) {
			config.push({
				title: 'Options',
				dataSource: 'options',
				width: 120,
				onRenderProperty: (item) => {
					return (
						<ButtonGroup variant='text' size='small'>
							<Tooltip component='span' title='Remove Spare Part'>
								<Button onClick={() => props.onRemoveSparePart(item)}>
									<DeleteIcon color='action' />
								</Button>
							</Tooltip>
						</ButtonGroup>
					)
				}
			})
		}
		if (status !== SpareParRequestStatus.DRAFT) {
			config.push({
				title: 'Options',
				dataSource: 'Options',
				width: 120,
				onRenderProperty: (item) => {
					let changeButton = onRenderButtonCollapse(item)
					const children = props.spareParts.filter((sp) => sp.ParentId === item.Id)
					const { totalParcialidad } = getPartialTotals(item, children)
					// A cancelled parent can no longer receive new partialities.
					const isParentCancelled = item.SparePartSaleStatus === 2
					// The parent must always keep at least 1 unit of its own (so it never drops to a
					// zero-quantity row in the index/report, which we're not touching in this change).
					const canAddMorePartialities = !isParentCancelled && item.Quantity > 1 && totalParcialidad > 1
					return (
						<ButtonGroup variant='text' size='small'>
							<Tooltip component='span' title='Options'>
								<Button onClick={() => onEditLateralPanelConfig(item)}>
									<MoreVertIcon color='primary' />
								</Button>
							</Tooltip>
							{status !== SpareParRequestStatus.CLOSED && ShowAddPartiality() ? (
								<Tooltip
									component='span'
									title={
										isParentCancelled
											? 'Cannot add partialities to a cancelled item'
											: item.Quantity <= 1
											? 'Quantity must be greater than 1'
											: !canAddMorePartialities
											? 'The parent must keep at least 1 unit'
											: 'Add partialities'
									}
								>
									<span>
										<Button onClick={() => openPartialityDialog(item)} disabled={isSaving || !canAddMorePartialities}>
											<Add color={canAddMorePartialities ? 'action' : 'disabled'} />
										</Button>
									</span>
								</Tooltip>
							) : null}
							{isAuthorizerCanBeGetExistencesFromSap && (
								<Tooltip component='span' title='Get Stock'>
									<IconButton size='small' color={changeButton ? 'primary' : 'inherit'} aria-label='stock' onClick={() => onExpandedPanelClick(item)}>
										<StorageIcon fontSize='small' />
									</IconButton>
								</Tooltip>
							)}
						</ButtonGroup>
					)
				}
			})
		}

		// Partiality expand/collapse icon column
		config.unshift({
			title: '',
			dataSource: 'ExpandPartialities',
			width: 40,
			onRenderProperty: (item) => {
				const hasChildren = props.spareParts.some((sp) => sp.ParentId === item.Id)
				if (!hasChildren) return null
				const isExpanded = expandedParents.has(item.Id)
				return (
					<IconButton
						size='small'
						onClick={(e) => {
							e.stopPropagation()
							toggleParentExpanded(item.Id)
						}}
					>
						{isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				)
			}
		})

		return config
	}

	const getChildColumnsInternal = () => {
		let config = childColumnsInternal.map((col) => ({ ...col }))

		// Blank alignment placeholders must reserve the same width as their parent-row counterpart;
		// an empty cell with no width causes the browser to collapse that column and let a neighboring
		// cell with real content (e.g. Total) expand and break the parent/child column alignment.
		// Only ExpandIcon and Part Number have no child-side equivalent to show — Position/PO#/Reference
		// ARE cascaded onto the child's own record by the backend, so they get real read-only renders below.
		const alignmentWidths = {
			_alignExpandIcon: '40px',
			_alignPartNumber: '150px'
		}
		Object.keys(alignmentWidths).forEach((dataSource) => {
			let placeholderProperty = config.find((x) => x.dataSource === dataSource)
			if (placeholderProperty) {
				placeholderProperty.onRenderProperty = () => <div style={{ width: alignmentWidths[dataSource] }} />
			}
		})

		// Read-only by design: Position/PO#/Reference are parent-level fields cascaded down to every
		// child (like Warehouse); the child inherits the value silently and must not edit its own copy —
		// to change it, edit the parent's control instead.
		let childPositionProperty = config.find((x) => x.dataSource === 'Position')
		childPositionProperty.onRenderProperty = (item) => {
			return <div style={{ width: '60px' }}>{item.Position}</div>
		}

		let childPoNumberProperty = config.find((x) => x.dataSource === 'PoNumber')
		childPoNumberProperty.onRenderProperty = (item) => {
			return <div style={{ width: '100px' }}>{item.PoNumber}</div>
		}

		let childReferenceSAPProperty = config.find((x) => x.dataSource === 'ReferenceSAP')
		childReferenceSAPProperty.onRenderProperty = (item) => {
			return <div style={{ width: '100px' }}>{item.ReferenceSAP}</div>
		}

		let sparePartSaleStatusProperty = config.find((x) => x.dataSource === 'SparePartSaleStatus')
		sparePartSaleStatusProperty.onRenderProperty = (item) => {
			let canBeEditStatus =
				isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<div style={{ width: '115px' }}>
					<Select
						key={'SparePartSaleStatus'}
						fullWidth
						defaultValue={item.SparePartSaleStatus}
						options={sparePartSaleStatusOptions}
						name='SparePartSaleStatus'
						isDisabled={!canBeEditStatus}
						value={item.SparePartSaleStatus ? sparePartSaleStatusOptions.find((x) => x.value === item.SparePartSaleStatus) : null}
						onChange={(name, option) => updateSpartPart(item, 'SparePartSaleStatus', option.value)}
						margin='dense'
						register={null}
						errors={errors}
						control={control}
					/>
				</div>
			)
		}

		// Read-only by design: Warehouse is a parent-level field cascaded down to every child (like
		// Position/PO#/Reference); the child inherits it silently and must not edit its own copy — to
		// change it, edit the parent's Warehouse control instead.
		let warehouseProperty = config.find((x) => x.dataSource === 'Warehouse')
		warehouseProperty.onRenderProperty = (item) => {
			const warehouseLabel = item.Warehouse ? warehouseOptions.find((x) => x.value === item.Warehouse)?.label : ''
			return <div style={{ width: '90px' }}>{warehouseLabel}</div>
		}

		let childUnitPriceProperty = config.find((x) => x.dataSource === 'UnitPrice')
		childUnitPriceProperty.onRenderProperty = (item) => {
			return <div style={{ width: '80px' }}>{item.UnitPrice}</div>
		}

		let childTotalProperty = config.find((x) => x.dataSource === 'Total')
		childTotalProperty.onRenderProperty = (item) => {
			return <div style={{ width: '80px' }}>{item.Total}</div>
		}

		// Read-only by design: an existing partiality's quantity must not be edited inline, since that
		// would bypass the validated "Add Partialities" flow (its remaining/max-quantity checks) and
		// could break the invariant that the parent always keeps at least 1 unit of its own.
		let partialQuantityProperty = config.find((x) => x.dataSource === 'PartialQuantity')
		partialQuantityProperty.onRenderProperty = (item) => {
			return <div style={{ width: '60px' }}>{item.PartialQuantity}</div>
		}

		let numberTrackingProperty = config.find((x) => x.dataSource === 'NumberTracking')
		numberTrackingProperty.onRenderProperty = (item) => {
			let editCanbe = isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<TextField
					style={{ width: '160px' }}
					key={'numberTraking'}
					fullWidth
					disabled={!editCanbe}
					defaultValue={item.NumberTracking}
					onBlur={(event) => updateSpartPart(item, 'NumberTracking', event.target.value)}
					margin='dense'
					variant='outlined'
					inputProps={{
						style: { fontSize: 12 }
					}}
				/>
			)
		}

		let statusProperty = config.find((x) => x.dataSource === 'Status')
		statusProperty.onRenderProperty = (item) => {
			const chipColor = getStatusColor(item.Status)
			return (
				<Chip
					label={chipColor === 'lightgray' ? 'Not found' : item?.Status}
					style={{
						backgroundColor: chipColor,
						color: 'white'
					}}
				/>
			)
		}

		let carrierProperty = config.find((x) => x.dataSource === 'Carrier')
		carrierProperty.onRenderProperty = (item) => {
			let editCanbe = isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<div style={{ width: '120px' }}>
					<Select
						key={'Carrier'}
						fullWidth
						defaultValue={item.Carrier}
						options={carriersOptions}
						name='Carrier'
						isDisabled={!editCanbe}
						value={item.Carrier ? carriersOptions.find((x) => x.value === item.Carrier || x.oldvalue === item.Carrier) : null}
						onChange={(name, option) => updateSpartPart(item, 'Carrier', option.value)}
						margin='dense'
						register={null}
						errors={errors}
						control={control}
					/>
					{item.IsOtherCarrier && (
						<TextField
							key={'otherCarrier'}
							fullWidth
							disabled={!editCanbe}
							defaultValue={item.OtherCarrier}
							onBlur={(event) => updateSpartPart(item, 'OtherCarrier', event.target.value)}
							margin='dense'
							variant='outlined'
							inputProps={{
								style: { fontSize: 12 }
							}}
						/>
					)}
				</div>
			)
		}

		let shipDateProperty = config.find((x) => x.dataSource === 'ShipDate')
		shipDateProperty.onRenderProperty = (item) => {
			let editCanbe = isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<div style={{ width: '100px' }}>
					<DateSelector
						showIncon={true}
						dataCy='date'
						name='shipDate'
						value={item.ShipDate}
						disabled={!editCanbe}
						onChange={(event) => updateSpartPart(item, 'ShipDate', event.target.value)}
					/>
				</div>
			)
		}

		let lastUpdateProperty = config.find((x) => x.dataSource === 'LastUpdateUser')
		lastUpdateProperty.onRenderProperty = (item) => {
			return (
				<div style={{ width: '120px' }}>
					<Typography variant='caption'>{`${item.LastUpdateUserName} ${item.LastUpdateDate}`}</Typography>
				</div>
			)
		}

		// Child options: lateral panel + delete partiality
		if (status !== SpareParRequestStatus.DRAFT) {
			config.push({
				title: 'Options',
				dataSource: 'Options',
				width: 120,
				onRenderProperty: (item) => {
					return (
						<ButtonGroup variant='text' size='small'>
							<Tooltip component='span' title='Options'>
								<Button onClick={() => onEditLateralPanelConfig(item)}>
									<MoreVertIcon color='primary' />
								</Button>
							</Tooltip>
							{status !== SpareParRequestStatus.CLOSED && ShowAddPartiality() ? (
								!item.IsComplete ? (
									<Tooltip component='span' title='Delete partiality'>
										<Button onClick={() => props.onRemoveSparePart(item)} disabled={isSaving}>
											<Delete color='error' />
										</Button>
									</Tooltip>
								) : (
									<Tooltip component='span' title='Cannot delete a partiality completed'>
										<Button>
											<Delete color='action' />
										</Button>
									</Tooltip>
								)
							) : null}
						</ButtonGroup>
					)
				}
			})
		}

		return config
	}

	const getTableConfigurationCustomer = () => {
		let enhancedTableConfig = tableConfig.map((col) => ({ ...col }))
		if (isAuthorizadoForEditedData && status === SpareParRequestStatus.DRAFT) {
			enhancedTableConfig.push({
				title: 'Options',
				dataSource: 'options',
				onRenderProperty: (item) => {
					return (
						<ButtonGroup variant='text' size='small'>
							{isAuthorizadoForEditedData && (
								<Tooltip component='span' title='Remove Spare Part'>
									<Button
										onClick={() => props.onRemoveSparePart(item)}
										disabled={props.isSaving && item.Token === props.identifierSparePartDelete}
									>
										<DeleteIcon color='action' />
									</Button>
								</Tooltip>
							)}
						</ButtonGroup>
					)
				}
			})
		}

		let unitPriceProperty = enhancedTableConfig.find((x) => x.dataSource === 'UnitPrice')
		unitPriceProperty.onRenderProperty = (item) => {
			return !item.IsPartiality ? item.UnitPrice : null
		}

		let unitTotalProperty = enhancedTableConfig.find((x) => x.dataSource === 'Total')
		unitTotalProperty.onRenderProperty = (item) => {
			return !item.IsPartiality ? item.Total : null
		}

		let sparePartSaleStatusProperty = enhancedTableConfig.find((x) => x.dataSource === 'SparePartSaleStatus')
		if (sparePartSaleStatusProperty) {
			sparePartSaleStatusProperty.onRenderProperty = (item) => {
				let canBeEditStatus =
					isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
				return (
					<div style={{ width: '115px' }}>
						<Select
							key={'SparePartSaleStatus'}
							fullWidth
							defaultValue={item.SparePartSaleStatus}
							options={sparePartSaleStatusOptions}
							name='SparePartSaleStatus'
							isDisabled={!canBeEditStatus}
							value={item.SparePartSaleStatus ? sparePartSaleStatusOptions.find((x) => x.value === item.SparePartSaleStatus) : null}
							onChange={(name, option) => updateSpartPart(item, 'SparePartSaleStatus', option.value)}
							margin='dense'
							register={null}
							errors={errors}
							control={control}
						/>
					</div>
				)
			}
		}
		let carrierProperty = enhancedTableConfig.find((x) => x.dataSource === 'Carrier')
		carrierProperty.onRenderProperty = (item) => {
			let editCanbe = isAdmin || (isAuthorizerCanEditRoleImberaMx && item.Warehouse === 1) || (isAuthorizerCanEditRoleImberaUsa && item.Warehouse === 2)
			return (
				<div style={{ width: '120px' }}>
					<Select
						key={'Carrier'}
						fullWidth
						defaultValue={item.Carrier}
						options={carriersOptions}
						name='Carrier'
						isDisabled={!editCanbe}
						value={item.Carrier ? carriersOptions.find((x) => x.label === item.Carrier) : null}
						onChange={(name, option) => updateSpartPart(item, 'Carrier', option.label)}
						margin='dense'
						register={null}
						errors={errors}
						control={control}
					/>
					{item.IsOtherCarrier && (
						<TextField
							key={'otherCarrier'}
							fullWidth
							disabled={!editCanbe}
							defaultValue={item.OtherCarrier}
							onBlur={(event) => updateSpartPart(item, 'OtherCarrier', event.target.value)}
							margin='dense'
							variant='outlined'
							inputProps={{
								style: { fontSize: 12 }
							}}
						/>
					)}
				</div>
			)
		}

		return enhancedTableConfig
	}

	const onRenderButtonCollapse = (item) => {
		let index = identifierExpanded.includes(item.Code)
		return index
	}

	const onExpandedPanelClick = (item) => {
		let index = identifierExpanded.includes(item.Code)
		if (index) {
			let newList = identifierExpanded.filter((identifier) => {
				return identifier !== item.Code
			})
			setIdentifierExpanded(newList)
		} else {
			let identifier = item.Code
			setIdentifierExpanded(identifierExpanded.concat(identifier))
		}
	}

	const renderPartialityDialog = () => {
		if (!partialityDialog.open || !partialityDialog.item) return null
		const item = partialityDialog.item
		const children = props.spareParts.filter((sp) => sp.ParentId === item.Id)
		const existingChildren = children.length
		const { totalSolicitada, totalParcialidad: remaining } = getPartialTotals(item, children)
		// The parent must always keep at least 1 unit for itself (so the index/report never see it drop
		// to a zero-quantity row), so a new partiality can take at most remaining - 1.
		const maxPartialityQuantity = remaining - 1
		return (
			<DialogView
				open={partialityDialog.open}
				onClose={closePartialityDialog}
				isSaving={isSaving}
				header={<span>Add Partialities</span>}
				actions={
					<Button
						onClick={handleAddPartialities}
						color='primary'
						variant='contained'
						disabled={partialityQuantity < 1 || partialityQuantity > maxPartialityQuantity || maxPartialityQuantity <= 0}
					>
						CONFIRM
					</Button>
				}
			>
				<div style={{ minWidth: '400px', padding: '8px 0' }}>
					<AlertComponent
						severityType='warning'
						variant='outlined'
						title={`${item.Code} | ${item.Description}`}
						message={`Requested: ${totalSolicitada} | Existing partialities: ${existingChildren} | Remaining: ${remaining}`}
					/>
					{maxPartialityQuantity > 0 ? (
						<div style={{ marginTop: '20px' }}>
							<TextField
								label='Quantity to send'
								type='number'
								value={partialityQuantity}
								onChange={(e) => {
									const val = parseInt(e.target.value) || 0
									setPartialityQuantity(Math.min(Math.max(1, val), maxPartialityQuantity))
								}}
								inputProps={{ min: 1, max: maxPartialityQuantity }}
								helperText={`The parent keeps at least 1 unit for itself; max you can send now is ${maxPartialityQuantity}. The parent's Remaining Qty will become ${remaining} - ${
									partialityQuantity < 1 ? 0 : partialityQuantity
								} = ${Math.max(1, remaining - (partialityQuantity < 1 ? 0 : partialityQuantity))}`}
								fullWidth
								variant='outlined'
							/>
						</div>
					) : (
						<AlertComponent
							severityType='error'
							variant='outlined'
							title='No remaining quantity'
							message='The parent must keep at least 1 unit for itself — that last unit ships directly on the parent row, not as a new partiality.'
						/>
					)}
				</div>
			</DialogView>
		)
	}

	const parentHeaders = isCustomerUser ? getTableConfigurationCustomer() : getParentColumnsInternal()
	const childHeadersConfig = isCustomerUser ? null : getChildColumnsInternal()

	const expandedPanelComponent = (item) => {
		return (
			<ExistencesTable
				columnSpam={parentHeaders.length}
				item={item}
				identifierPanel={identifierExpanded}
				isSaving={isGetStock}
				updateType={'opened'}
				sparePartSales={true}
			/>
		)
	}
	return (
		<Fragment>
			{!isCustomerUser && (
				<div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px' }}>
					<ButtonGroup size='small' variant='text'>
						<Button onClick={expandAllParents}>Expand all</Button>
						<Button onClick={collapseAllParents}>Collapse all</Button>
					</ButtonGroup>
				</div>
			)}
			<DatagridCustom
				headers={parentHeaders}
				childHeaders={childHeadersConfig}
				data={props.spareParts}
				isExpandedPanel={true}
				expandedPanelComponent={expandedPanelComponent}
				expandedParents={expandedParents}
			/>
			{renderPartialityDialog()}
		</Fragment>
	)
}

SparePartList.propTypes = {
	/**
	 * Callback executed when the user request to add a new spare part
	 */
	onAddSparePartRequest: propTypes.func,
	/**
	 * Callback executed when removing a spare part
	 */
	onRemoveSparePart: propTypes.func,
	/**
	 * Callback executed when click en edit comments
	 */
	onEditLateralPanelConfig: propTypes.func.isRequired
}

export default SparePartList

const tableConfig = [
	{
		title: 'Code',
		dataSource: 'Code'
	},
	{
		title: 'Description',
		dataSource: 'Description'
	},
	{
		title: 'Quality',
		dataSource: 'Quality'
	},
	{
		title: 'Price',
		dataSource: 'UnitPrice'
	},
	{
		title: 'Total',
		dataSource: 'Total'
	},
	{
		title: 'Tracking number',
		dataSource: 'NumberTracking'
	},
	{
		title: 'Carrier',
		dataSource: 'Carrier'
	},
	{
		title: 'ETA',
		dataSource: 'ShipDate'
	},
	{
		title: 'Status',
		dataSource: 'Status'
	}
]

// Parent and child column sets are kept in the same positional order so partiality rows
// (rendered in a separate nested table) visually line up under the parent's columns above them.
// Position/PO#/Reference/Warehouse are cascaded onto each child's own record by the backend, so the
// child side renders them as read-only inherited text. ExpandIcon and Part Number have no child-side
// equivalent and use an '_align*' placeholder dataSource that never matches any item field, rendering
// as an empty cell purely to preserve column position.
// Column widths are declared explicitly and kept identical between the parent's outer table and
// the child's nested table (see DataGridPartialities.js, which renders both with table-layout:
// fixed + a matching <colgroup>). Without explicit, shared widths, the two independently-laid-out
// tables size their columns from content alone and drift out of alignment.
const parentColumnsInternal = [
	{
		title: 'Part Number',
		dataSource: 'Description',
		width: 150
	},
	{
		title: 'Status',
		dataSource: 'SparePartSaleStatus',
		width: 115
	},
	{
		title: 'Remaining Qty',
		dataSource: 'TotalParcialidad',
		width: 60
	},
	{
		title: 'Position',
		dataSource: 'Position',
		width: 60
	},
	{
		title: 'Warehouse',
		dataSource: 'Warehouse',
		width: 90
	},
	{
		title: 'PO #',
		dataSource: 'PoNumber',
		width: 100
	},
	{
		title: 'Reference',
		dataSource: 'ReferenceSAP',
		width: 100
	},
	{
		title: 'Tracking number',
		dataSource: 'NumberTracking',
		width: 160
	},
	{
		title: 'Carrier',
		dataSource: 'Carrier',
		width: 120
	},
	{
		title: 'ETA',
		dataSource: 'ShipDate',
		width: 100
	},
	{
		title: 'T. Status',
		dataSource: 'Status',
		width: 100
	},
	{
		title: 'Price',
		dataSource: 'UnitPrice',
		width: 80
	},
	{
		title: 'Subtotal',
		dataSource: 'Total',
		width: 80
	},
	{
		title: 'Last modified',
		dataSource: 'LastUpdateUser',
		width: 140
	}
]

const childColumnsInternal = [
	{
		title: '',
		dataSource: '_alignExpandIcon',
		width: 40
	},
	{
		title: '',
		dataSource: '_alignPartNumber',
		width: 150
	},
	{
		title: 'Status',
		dataSource: 'SparePartSaleStatus',
		width: 115
	},
	{
		title: 'Partial Qty',
		dataSource: 'PartialQuantity',
		width: 60
	},
	{
		title: 'Position',
		dataSource: 'Position',
		width: 60
	},
	{
		title: 'Warehouse',
		dataSource: 'Warehouse',
		width: 90
	},
	{
		title: 'PO #',
		dataSource: 'PoNumber',
		width: 100
	},
	{
		title: 'Reference',
		dataSource: 'ReferenceSAP',
		width: 100
	},
	{
		title: 'Tracking number',
		dataSource: 'NumberTracking',
		width: 160
	},
	{
		title: 'Carrier',
		dataSource: 'Carrier',
		width: 120
	},
	{
		title: 'ETA',
		dataSource: 'ShipDate',
		width: 100
	},
	{
		title: 'T. Status',
		dataSource: 'Status',
		width: 100
	},
	{
		title: 'Price',
		dataSource: 'UnitPrice',
		width: 80
	},
	{
		title: 'Total',
		dataSource: 'Total',
		width: 80
	},
	{
		title: 'Last modified',
		dataSource: 'LastUpdateUser',
		width: 140
	}
]
